import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Grid from 'componentsV2/Grid';
import { Poster, SEARCH_POSTER } from 'componentsV2/Poster';
import { Tile, FIND_TILE, EPISODE_TILE } from 'componentsV2/Tile';
import Loader from 'components/Loader';
import { getSitePlatform } from 'helpers/BreakpointHelper';
import {
  isCategoryTypePersonality,
  CARD_TYPE_POSTER,
  CARD_TYPE_EPISODE,
} from 'helpers/ContentHelper';
import Styles from './Result.module.scss';

const Result = ({
  account,
  favoritesManager,
  fetching,
  items,
  loadMore,
  next,
  posterType,
  searchProps,
  theme,
  type,
}) => {
  const lastElementRef = useRef(null);
  const nextPage = useRef(next);
  const observer = useRef(
    new window.IntersectionObserver(entries => {
      const first = entries[0];
      if (first.isIntersecting) {
        loadMore(nextPage.current);
      }
    }),
  );

  useEffect(() => {
    nextPage.current = next;
  }, [next]);

  useEffect(() => {
    const currentElement = lastElementRef.current;
    const currentObserver = observer.current;

    if (currentElement) {
      currentObserver.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement);
      }
    };
  }, []);

  const breakpoint = getSitePlatform();
  const renderPoster = items =>
    items?.map((item, index) => (
      <Poster
        account={account}
        breakpoint={breakpoint}
        favoritesManager={favoritesManager}
        isGrid
        key={item?.id}
        isPersonality={isCategoryTypePersonality(item?.categoryType)}
        item={item}
        itemIndex={index}
        searchProps={searchProps}
        type={posterType || SEARCH_POSTER}
      />
    ));
  const renderTile = items =>
    items?.map((item, index) => (
      <Tile
        account={account}
        breakpoint={breakpoint}
        item={item}
        key={item?.id}
        itemIndex={index}
        searchProps={searchProps}
        type={FIND_TILE}
      />
    ));

  const renderEpisode = items =>
    items?.map((item, index) => (
      <Tile
        account={account}
        breakpoint={breakpoint}
        item={item}
        itemIndex={index}
        searchProps={searchProps}
        type={EPISODE_TILE}
      />
    ));

  const displayTiles = () => {
    if (type === CARD_TYPE_EPISODE) {
      return renderEpisode(items);
    }

    if (type === CARD_TYPE_POSTER) {
      return renderPoster(items);
    }

    return renderTile(items);
  };

  return (
    <>
      <Grid
        type={type}
        theme={
          searchProps?.searchValue
            ? {
                gridContainer: classNames(
                  Styles.gridContainer,
                  theme?.gridContainer,
                  Styles.gridContainerOpen,
                ),
              }
            : { gridContainer: classNames(Styles.gridContainer, theme?.gridContainer) }
        }
      >
        {displayTiles()}
      </Grid>
      <div
        ref={lastElementRef}
        aria-hidden
        className={classNames(items?.length ? Styles.showLastEle : Styles.hideLastEle)}
      />
      {fetching ? <Loader theme={Styles} /> : null}
    </>
  );
};

Result.propTypes = {
  account: PropTypes.shape({}),
  favoritesManager: PropTypes.shape({}),
  fetching: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  loadMore: PropTypes.func,
  next: PropTypes.string,
  posterType: PropTypes.string,
  searchProps: PropTypes.shape({}),
  theme: PropTypes.shape({}),
  type: PropTypes.string,
};

export default Result;
